// src/Pages/Hmo/CoverageDetails.js
// ✅ FIXED: Uses product's MinimumPremium from backend API instead of hardcoded value
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { authenticatedFetch } from '../../utils/api';

function CoverageDetails() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [paystackLoaded, setPaystackLoaded] = useState(false);
  const [productLoading, setProductLoading] = useState(true);  // ✅ NEW: Product fetch loading

  // Load applicant details from localStorage
  const [applicantDetails, setApplicantDetails] = useState({
    fullName: '',
    email: '',
    phone: ''
  });

  // ✅ NEW: Product details state
  const [productDetails, setProductDetails] = useState(null);
  const [basePremiumPerMonth, setBasePremiumPerMonth] = useState(30000);  // ✅ Fallback default

  // Brand Colors
  const PRIMARY_COLOR = '#F89423';
  const INFO_BG = '#F0FDF4';
  const INFO_BORDER = '#F89423';
  const INFO_TEXT = '#166534';
  const INFO_ICON = '#166534';
  const PREMIUM_BG = '#F0FDF4';
  const PREMIUM_TEXT = '#166534';

  // Paystack Config
  const PAYSTACK_PUBLIC_KEY = process.env.REACT_APP_PAYSTACK_PUBLIC_KEY || 'pk_test_f4d75082b401a553cdfb9ed7eb11ae2a7be9d099';

  // ✅ Extract ALL params including agent attribution
  const companyId = searchParams.get('company');
  const linkId = searchParams.get('link');
  const productId = searchParams.get('product');
  const agentId = searchParams.get('agent');  // ✅ Preserve agent
  const planOwnerId = searchParams.get('planOwnerId');  // ✅ From previous step

  const API_BASE = process.env.REACT_APP_API_URL || 'https://localhost:7018';

  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [premium, setPremium] = useState(0);

  const paymentPeriods = Array.from({ length: 12 }, (_, i) => ({
    value: (i + 1).toString(),
    label: `${i + 1} Month${i + 1 > 1 ? 's' : ''}`,
    months: i + 1
  }));

  // ✅ NEW: Fetch product details to get MinimumPremium
  useEffect(() => {
    const fetchProductDetails = async () => {
      try {
        setProductLoading(true);
        
        if (!companyId || !linkId || !productId) {
          throw new Error('Missing required parameters');
        }
        
        // ✅ Call your endpoint to get link with bundled products
        const response = await authenticatedFetch(
          `${API_BASE}/Settings/links/company/${companyId}?userType=hmo`,
          'POST',
          {}
        );
        
        if (!response?.success) {
          throw new Error(response?.message || 'Failed to fetch product details');
        }
        
        // ✅ Find the specific link and product
        const links = response.data || [];
        const targetLink = links.find(l => l.linkId === linkId);
        
        if (!targetLink) {
          throw new Error(`Link not found: ${linkId}`);
        }
        
        const bundledProducts = targetLink.bundledProducts || [];
        const targetProduct = bundledProducts.find(p => 
          String(p.Id || p.id) === String(productId)
        );
        
        if (!targetProduct) {
          throw new Error(`Product not found: ${productId}`);
        }
        
        // ✅ Extract MinimumPremium (handle both number and string formats)
        const minPremium = targetProduct.MinimumPremium ?? targetProduct.minimumPremium;
        const premiumValue = typeof minPremium === 'number' ? minPremium : parseFloat(minPremium) || 30000;
        
        console.log('✅ Product loaded:', {
          name: targetProduct.Name || targetProduct.name,
          minimumPremium: premiumValue
        });
        
        setProductDetails(targetProduct);
        setBasePremiumPerMonth(premiumValue);
        
      } catch (error) {
        console.error('💥 Error fetching product details:', error);
        toast.warning('Using default premium. Product details could not be loaded.');
        // Keep fallback default of 30000
        setBasePremiumPerMonth(30000);
      } finally {
        setProductLoading(false);
      }
    };
    
    if (companyId && linkId && productId) {
      fetchProductDetails();
    }
  }, [companyId, linkId, productId]);

  // Load applicant details on mount
  useEffect(() => {
    try {
      const savedDetails = localStorage.getItem('planOwnerDetails');
      if (savedDetails) {
        const parsed = JSON.parse(savedDetails);
        setApplicantDetails({
          fullName: parsed.personalName || '',
          email: parsed.personalEmail || '',
          phone: parsed.personalPhone || ''
        });
      } else {
        const queryParams = new URLSearchParams();
        if (companyId) queryParams.set('company', companyId);
        if (linkId) queryParams.set('link', linkId);
        if (productId) queryParams.set('product', productId);
        if (agentId) queryParams.set('agent', agentId);
        navigate(`/insurance/plan-owner?${queryParams.toString()}`, { replace: true });
      }
    } catch (error) {
      console.error('💥 Error loading applicant details:', error);
      toast.error('Failed to load your details. Please start over.');
      navigate('/insurance/plan-owner', { replace: true });
    }
  }, [navigate, companyId, linkId, productId, agentId]);

  // Load Paystack SDK
  useEffect(() => {
    if (window.PaystackPop) return setPaystackLoaded(true);

    const script = document.createElement('script');
    script.src = 'https://js.paystack.co/v1/inline.js';
    script.async = true;
    script.onload = () => setPaystackLoaded(true);
    script.onerror = () => {
      toast.error('Failed to load payment gateway. Check your internet connection.');
      setPaystackLoaded(false);
    };
    document.body.appendChild(script);

    return () => {
      const existing = document.querySelector('script[src="https://js.paystack.co/v1/inline.js"]');
      if (existing) existing.remove();
    };
  }, []);

  // ✅ UPDATED: Use dynamic basePremiumPerMonth from product
  const handlePeriodSelect = (period) => {
    setSelectedPeriod(period);
    setPremium(basePremiumPerMonth * period.months);  // ✅ Uses dynamic premium
    setShowDropdown(false);
  };

  const formatPrice = (price) => `₦${Number(price).toLocaleString()}`;

  const generateReference = () => `FSL-${Date.now()}-${Math.random().toString(36).substring(2, 11).toUpperCase()}`;

  // ✅ Handle payment success and redirect
  const handlePaymentSuccess = async (paystackResponse) => {
    try {
      // ✅ Save payment record to backend (optional but recommended)
      const paymentPayload = {
        companyId: companyId ? parseInt(companyId) : null,
        linkId: linkId,
        productId: productId ? parseInt(productId) : null,
        agentId: agentId,  // ✅ Preserve agent attribution
        planOwnerId: planOwnerId ? parseInt(planOwnerId) : null,
        
        reference: paystackResponse.reference,
        transactionId: paystackResponse.tranx_id,
        amount: premium,
        currency: 'NGN',
        status: 'success',
        paidAt: new Date().toISOString(),
        
        applicantEmail: applicantDetails.email,
        applicantPhone: applicantDetails.phone,
        periodMonths: selectedPeriod?.months,
        productPremium: basePremiumPerMonth,  // ✅ Include product premium for records
        meta: paystackResponse.meta || {}
      };

      console.log('📤 Recording payment:', paymentPayload);
      
      // Optional: Call backend to record payment
      // await authenticatedFetch(`${API_BASE}/Settings/record-payment`, 'POST', paymentPayload);
      
      // ✅ Clear sensitive data
      localStorage.removeItem('planOwnerDetails');
      
      // ✅ Navigate to success page with all params
      navigate('/insurance-links', {
        state: {
          paymentSuccess: true,
          reference: paystackResponse.reference,
          amount: premium,
          period: selectedPeriod?.label,
          applicantEmail: applicantDetails.email,
          productPremium: basePremiumPerMonth,  // ✅ Pass premium info
          timestamp: new Date().toISOString()
        },
        replace: true
      });
      
    } catch (error) {
      console.error('💥 Error recording payment:', error);
      // Still navigate even if backend call fails
      navigate('/insurance-links', {
        state: {
          paymentSuccess: true,
          reference: paystackResponse.reference,
          amount: premium
        },
        replace: true
      });
    }
  };

  // Open Paystack popup
  const openPaystackPopup = (reference, onSuccess, onClose) => {
    return new Promise((resolve, reject) => {
      if (!window.PaystackPop) return reject(new Error('Paystack not loaded'));

      const config = {
        key: PAYSTACK_PUBLIC_KEY,
        email: applicantDetails.email,
        amount: premium * 100, // in kobo
        currency: 'NGN',
        ref: reference,
        meta: {
          company_id: companyId,
          link_id: linkId,
          product_id: productId,
          agent_id: agentId,  // ✅ Include agent in metadata
          plan_owner_id: planOwnerId,
          period_months: selectedPeriod?.months,
          product_premium: basePremiumPerMonth,  // ✅ Include product premium
          applicant_name: applicantDetails.fullName,
          applicant_phone: applicantDetails.phone
        },
        callback: function(response) {
          resolve(response);
          onSuccess?.(response);
        },
        onClose: function() {
          reject(new Error('Payment cancelled'));
          onClose?.();
        }
      };

      window.PaystackPop.setup(config).openIframe();
    });
  };

  // Main payment handler
  const handlePayment = async () => {
    if (!selectedPeriod) return toast.warning('Please select a period of cover');
    if (!paystackLoaded) return toast.warning('Payment gateway is loading. Please wait...');
    if (!applicantDetails.email) return toast.warning('Email is required for payment');
    if (productLoading) return toast.warning('Loading product details. Please wait...');

    setLoading(true);
    const reference = generateReference();

    try {
      await openPaystackPopup(reference, handlePaymentSuccess, () => toast.info('Payment window closed'));
    } catch (error) {
      if (error.message === 'Payment cancelled') {
        toast.info('Payment cancelled by user');
      } else {
        console.error('💥 Payment error:', error);
        toast.error(error.message || 'Payment failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handlePayment();
  };

  const handleClose = () => {
    if (window.opener && !window.opener.closed) {
      window.close();
    } else {
      navigate('/', { replace: true });
    }
  };

  // ✅ Helper: Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDropdown && !event.target.closest('.period-dropdown')) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showDropdown]);

  // ✅ Helper: Render product premium info
  const renderPremiumInfo = () => {
    if (productLoading) {
      return (
        <div className="text-muted small">
          <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
          Loading premium...
        </div>
      );
    }
    
    if (productDetails) {
      return (
        <small className="text-muted d-block mt-1">
          {formatPrice(basePremiumPerMonth)} × {selectedPeriod?.months} month{selectedPeriod?.months > 1 ? 's' : ''}
          {productDetails.Name && ` • ${productDetails.Name}`}
        </small>
      );
    }
    
    return (
      <small className="text-muted d-block mt-1">
        {formatPrice(basePremiumPerMonth)} × {selectedPeriod?.months} month{selectedPeriod?.months > 1 ? 's' : ''}
      </small>
    );
  };

  return (
    <div className="min-vh-100 d-flex flex-column" style={{ backgroundColor: '#f8f9fa' }}>
      <ToastContainer position="top-center" autoClose={3000} hideProgressBar newestOnTop />

      {/* Header */}
      <header className="bg-white border-bottom py-3">
        <div className="container d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center">
            <img src="/images/fsl_logo.png" alt="FSL Logo" style={{ height: '40px' }} onError={(e) => { e.target.style.display = 'none'; }} />
            <span className="ms-2 fw-bold" style={{ color: '#111827' }}>FSL HMO</span>
          </div>
          <button className="btn btn-light rounded-circle d-flex align-items-center justify-content-center" style={{ width: '36px', height: '36px' }} onClick={handleClose} aria-label="Close">
            <i className="fas fa-times"></i>
          </button>
        </div>
      </header>

      {/* Main */}
      <main className="flex-grow-1 py-5">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-6 col-md-8">
              <div className="text-center mb-4">
                <h1 className="fw-bold" style={{ fontSize: '24px', color: '#111827' }}>Coverage Details</h1>
                <p className="text-muted small mb-0">Complete your HMO plan purchase</p>
                {/* ✅ Agent Attribution Badge */}
                {agentId && (
                  <div className="mt-2">
                    <span className="badge" style={{ backgroundColor: `${PRIMARY_COLOR}15`, color: PRIMARY_COLOR, border: `1px solid ${INFO_BORDER}`, padding: '6px 12px', fontSize: '12px', fontWeight: '500' }}>
                      <i className="fas fa-user-tag me-1"></i>
                      Agent: {agentId.substring(0, 8)}...
                    </span>
                  </div>
                )}
              </div>

              <div className="rounded-3 p-4 shadow-sm" style={{ backgroundColor: 'white' }}>
                <div className="rounded-3 p-3 mb-4 d-flex align-items-start gap-3" style={{ backgroundColor: INFO_BG, border: `1px solid ${INFO_BORDER}` }}>
                  <i className="fas fa-info-circle mt-1" style={{ color: INFO_ICON, fontSize: '18px', flexShrink: 0 }}></i>
                  <p className="mb-0" style={{ fontSize: '14px', color: INFO_TEXT }}>Review your details and select your coverage period.</p>
                </div>

                <form onSubmit={handleSubmit} noValidate>
                  {/* Full Name */}
                  <div className="mb-3">
                    <label className="form-label fw-semibold" style={{ fontSize: '14px' }}>Full Name <span className="text-danger">*</span></label>
                    <input type="text" className="form-control" value={applicantDetails.fullName || ''} readOnly style={{ border: '2px solid #E5E7EB', borderRadius: '8px', padding: '10px 12px', backgroundColor: '#F9FAFB', cursor: 'not-allowed' }} />
                    <small className="text-muted" style={{ fontSize: '12px' }}>
                      From previous step • <button type="button" className="btn btn-link p-0" style={{ fontSize: '12px', color: PRIMARY_COLOR }} onClick={() => navigate('/insurance/plan-owner')}>Edit details</button>
                    </small>
                  </div>

                  {/* Email */}
                  <div className="mb-3">
                    <label className="form-label fw-semibold" style={{ fontSize: '14px' }}>Email Address <span className="text-danger">*</span></label>
                    <input type="email" className="form-control" value={applicantDetails.email || ''} readOnly style={{ border: '2px solid #E5E7EB', borderRadius: '8px', padding: '10px 12px', backgroundColor: '#F9FAFB', cursor: 'not-allowed' }} />
                  </div>

                  {/* Phone */}
                  <div className="mb-4">
                    <label className="form-label fw-semibold" style={{ fontSize: '14px' }}>Phone Number <span className="text-danger">*</span></label>
                    <input type="tel" className="form-control" value={applicantDetails.phone || ''} readOnly style={{ border: '2px solid #E5E7EB', borderRadius: '8px', padding: '10px 12px', backgroundColor: '#F9FAFB', cursor: 'not-allowed' }} />
                  </div>

                  {/* Period - With proper dropdown handling */}
                  <div className="mb-4 period-dropdown">
                    <label className="form-label fw-semibold" style={{ fontSize: '14px' }}>Period Of Cover <span className="text-danger">*</span></label>
                    <div className="position-relative">
                      <div 
                        onClick={() => !loading && !productLoading && setShowDropdown(!showDropdown)} 
                        className="form-control" 
                        style={{ 
                          backgroundColor: (loading || productLoading) ? '#f3f4f6' : 'white', 
                          border: `2px solid ${showDropdown ? PRIMARY_COLOR : '#E5E7EB'}`, 
                          borderRadius: '8px', 
                          padding: '10px 12px', 
                          cursor: (loading || productLoading) ? 'not-allowed' : 'pointer', 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center', 
                          opacity: (loading || productLoading) ? 0.7 : 1 
                        }}
                      >
                        <span style={{ color: selectedPeriod ? '#111827' : '#6b7280' }}>
                          {selectedPeriod ? selectedPeriod.label : (productLoading ? 'Loading plans...' : 'Select payment plan')}
                        </span>
                        <i className={`fas fa-chevron-${showDropdown ? 'up' : 'down'}`} style={{ color: '#6b7280', fontSize: '12px' }}></i>
                      </div>

                      {showDropdown && (
                        <div className="position-absolute w-100 mt-1 rounded-3 shadow" style={{ backgroundColor: 'white', border: `1px solid #E5E7EB`, zIndex: 1000, maxHeight: '250px', overflowY: 'auto' }}>
                          {paymentPeriods.map((period) => (
                            <div 
                              key={period.value} 
                              onClick={() => !loading && !productLoading && handlePeriodSelect(period)} 
                              className="p-3" 
                              style={{ 
                                cursor: (loading || productLoading) ? 'not-allowed' : 'pointer', 
                                backgroundColor: selectedPeriod?.value === period.value ? INFO_BG : 'white', 
                                color: selectedPeriod?.value === period.value ? PRIMARY_COLOR : '#111827', 
                                fontSize: '14px', 
                                borderBottom: '1px solid #f3f4f6' 
                              }}
                            >
                              {period.label}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Premium - Now uses dynamic MinimumPremium */}
                  <div className="mb-4">
                    <label className="form-label fw-semibold" style={{ fontSize: '14px' }}>Premium</label>
                    <div className="rounded-3 p-3" style={{ backgroundColor: PREMIUM_BG, border: `1px solid ${INFO_BORDER}` }}>
                      <h4 className="mb-0 fw-bold" style={{ color: PREMIUM_TEXT }}>
                        {premium > 0 ? formatPrice(premium) : '₦0'}
                      </h4>
                      {premium > 0 && renderPremiumInfo()}
                    </div>
                    {/* ✅ Show product name if loaded */}
                    {productDetails?.Name && (
                      <small className="text-muted d-block mt-2">
                        Product: <strong>{productDetails.Name}</strong>
                      </small>
                    )}
                  </div>

                  {/* Pay Button */}
                  <button 
                    type="submit" 
                    disabled={loading || !selectedPeriod || !paystackLoaded || productLoading} 
                    className="btn w-100 py-3 fw-semibold rounded-3 d-flex align-items-center justify-content-center gap-2" 
                    style={{ 
                      backgroundColor: PRIMARY_COLOR, 
                      color: 'white', 
                      border: 'none', 
                      opacity: loading || !selectedPeriod || !paystackLoaded || productLoading ? 0.7 : 1, 
                      cursor: loading || !selectedPeriod || !paystackLoaded || productLoading ? 'not-allowed' : 'pointer', 
                      transition: 'all 0.3s ease', 
                      fontSize: '16px' 
                    }}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                        Processing...
                      </>
                    ) : !paystackLoaded ? (
                      <>
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                        Loading Payment...
                      </>
                    ) : productLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                        Loading Premium...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-lock me-1"></i>
                        Pay {formatPrice(premium)}
                      </>
                    )}
                  </button>

                  <p className="text-center text-muted small mt-3 mb-0">
                    <i className="fas fa-shield-alt me-1" style={{ color: PRIMARY_COLOR }}></i> Secured by Paystack • 256-bit SSL Encryption
                  </p>
                  <p className="text-center text-muted small mt-2 mb-0">
                    By proceeding, you agree to our <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: PRIMARY_COLOR, textDecoration: 'none' }}>Terms of Service</a>
                  </p>
                </form>
              </div>

              <div className="text-center mt-4">
                <small className="text-muted">Need help? Contact <a href="mailto:support@fsl.ng" style={{ color: PRIMARY_COLOR }}>support@fsl.ng</a></small>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-top py-3 mt-auto">
        <div className="container text-center">
          <p className="text-muted small mb-0"><i className="fas fa-shield-alt me-1" style={{ color: PRIMARY_COLOR }}></i>Powered by <span style={{ fontWeight: '600', color: PRIMARY_COLOR }}>FSL</span></p>
          <p className="text-muted small mb-0 mt-1">© {new Date().getFullYear()} FSL Management Services. All rights reserved.</p>
        </div>
      </footer>

      {/* Overlay to close dropdown */}
      {showDropdown && (
        <div 
          className="position-fixed top-0 start-0 w-100 h-100" 
          style={{ zIndex: 999, backgroundColor: 'transparent' }} 
          onClick={() => setShowDropdown(false)} 
          aria-hidden="true"
        />
      )}
    </div>
  );
}

export default CoverageDetails;